/**
 * Lista as autorizações vinculadas ao CPF do estudante logado.
 *
 * Reutiliza os mesmos endpoints do Acompanhamento:
 *   - TCE (programa 0):           GET /tce/estudante/<cpf>/autorizacoes
 *   - TCA Empregador (programa 2): GET /tca-empregador/estudante/<cpf>/autorizacoes
 *
 * O objetivo é alimentar o seletor de "Autorização" na página de
 * Histórico. Cada item retornado representa UMA autorização individual,
 * permitindo que o estudante visualize o histórico de um processo por
 * vez, sem misturar registros de várias autorizações que o mesmo CPF
 * possui.
 *
 * O payload do backend pode variar:
 *   - `id` ou `tca_id` ou `tce_id` como identificador da autorização
 *   - `nome` / `razao_social` / `nome_fantasia` como descrição
 *   - `documento` / `cnpj` / `cpf` como vínculo
 *   - `tipo_programa` (0 ou 2) para sabermos se é TCE ou TCA-Empregador
 *
 * Esta função devolve a lista num formato canônico:
 *   [{ id, tipoPrograma, nome, documento, raw }]
 *
 * Falha silenciosa em qualquer um dos endpoints (não quebra a UI).
 */

import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../../js/api.js";

function resolverCpfEstudante(token) {
  let decoded = null;
  try {
    decoded = jwtDecode(token || "");
  } catch (_) {
    return "";
  }
  if (!decoded) return "";
  const bruto = String(
    decoded.cpf || decoded.documento || decoded.sub || "",
  );
  return bruto.replace(/\D/g, "").slice(0, 11);
}

function resolverTipoPrograma(token) {
  let decoded = null;
  try {
    decoded = jwtDecode(token || "");
  } catch (_) {
    return null;
  }
  if (!decoded) return null;
  const n = Number(decoded.tipo_programa);
  return Number.isFinite(n) ? n : null;
}

function extrairLista(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    for (const chave of ["data", "rows", "items", "itens", "results"]) {
      if (Array.isArray(payload[chave])) return payload[chave];
    }
  }
  return [];
}

function normalizarItem(item, tipoProgramaPadrao) {
  if (!item || typeof item !== "object") return null;

  const id =
    Number(
      item.id ??
        item.tca_id ??
        item.tcaId ??
        item.tce_id ??
        item.tceId ??
        item.tca_empregador_id ??
        0,
    ) || null;

  const tipoPrograma = Number(
    item.tipo_programa ?? item.tipoPrograma ?? tipoProgramaPadrao ?? 0,
  );

  const nome =
    item.nome ||
    item.nome_completo ||
    item.razao_social ||
    item.razaoSocial ||
    item.nome_fantasia ||
    item.nomeFantasia ||
    item.estudante_nome ||
    item.empresa_nome ||
    `Autorização #${id ?? "?"}`;

  const documento =
    item.documento ||
    item.cnpj ||
    item.cnpj_ou_cpf ||
    item.cpf ||
    item.cpf_estudante ||
    item.estudante_cpf ||
    item.cnpj_empresa ||
    "";

  return {
    id,
    tipoPrograma,
    nome: String(nome).trim(),
    documento: String(documento).trim(),
    raw: item,
  };
}

/**
 * Lista todas as autorizações do estudante logado.
 *
 * @param {string} token JWT do usuário logado.
 * @returns {Promise<Array<{id, tipoPrograma, nome, documento, raw}>>}
 */
export async function listarAutorizacoesDoEstudante(token) {
  const t =
    token ||
    (typeof window !== "undefined"
      ? window.localStorage.getItem("token")
      : null);
  if (!t) return [];

  const cpf = resolverCpfEstudante(t);
  if (!cpf) return [];

  const tipoProgramaJwt = resolverTipoPrograma(t);

  // Regra do card do Trello: o estudante entra no fluxo de Acompanhamento/
  // Histórico apenas para tipo_programa 0 (TCE) e 2 (TCA Empregador).
  const endpoints = [];
  if (tipoProgramaJwt === 0) {
    endpoints.push({
      url: `${API_BASE_URL}/tce/estudante/${cpf}/autorizacoes`,
      tipo: 0,
    });
  } else if (tipoProgramaJwt === 2) {
    endpoints.push({
      url: `${API_BASE_URL}/tca-empregador/estudante/${cpf}/autorizacoes`,
      tipo: 2,
    });
  } else {
    // Fallback defensivo: se o JWT não trouxer `tipo_programa` (legado),
    // tentamos os dois endpoints. Se a API bloquear (403), seguimos.
    endpoints.push({
      url: `${API_BASE_URL}/tce/estudante/${cpf}/autorizacoes`,
      tipo: 0,
    });
    endpoints.push({
      url: `${API_BASE_URL}/tca-empregador/estudante/${cpf}/autorizacoes`,
      tipo: 2,
    });
  }

  const agregada = [];
  for (const { url, tipo } of endpoints) {
    try {
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${t}` },
        timeout: 15000,
      });
      const lista = extrairLista(resp?.data);
      for (const item of lista) {
        const normalizado = normalizarItem(item, tipo);
        if (normalizado) agregada.push(normalizado);
      }
    } catch (err) {
      // Falha em um endpoint NÃO deve matar o outro. Apenas loga.
      // eslint-disable-next-line no-console
      console.warn(
        "[HistoricoEventos/autorizacoesEstudante] falha ao buscar",
        url,
        err?.response?.status || err?.message,
      );
    }
  }

  // Deduplica por id+tipoPrograma (defesa contra payload duplicado).
  const mapa = new Map();
  for (const it of agregada) {
    if (!it.id) continue;
    const chave = `${it.tipoPrograma}::${it.id}`;
    if (!mapa.has(chave)) mapa.set(chave, it);
  }
  return Array.from(mapa.values());
}

/**
 * Verifica se o JWT pertence a um estudante do fluxo TCE/TCA Empregador.
 *
 * @param {string} token
 * @returns {boolean}
 */
export function tokenEhDeEstudante(token) {
  let decoded = null;
  try {
    decoded = jwtDecode(token || "");
  } catch (_) {
    return false;
  }
  if (!decoded) return false;
  if (decoded.tipo === "estudante") return true;
  if (Number(decoded.acesso_autorizacao ?? 0) === 1) return true;
  return false;
}
